import { CircularProgress, IconButton } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Check, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../../hooks/global/typedReduxHooks';
import InternalCommentObject from '../../../../../../../interfaces/InternalCommentsObject';
import investigatorNotesServices from '../../../../../../../services/investigatorNotes';
import meetingService from '../../../../../../../services/meetingService';
import { updateReportInternalComments } from '../../../../../../../slices/reportDataSlice';
import { setInvestigatorNoteCategories } from '../../../../../../../slices/reportSlice';

interface CatergoryOption {
  id: number;
  name: string;
  isNewOption?: boolean;
}

interface InternalCommentEditCategoryFormProps {
  internalComment: InternalCommentObject;
  selectedCategory: CatergoryOption | null;
  onComplete: () => void;
}

function InternalCommentEditCategoryForm({
  internalComment,
  selectedCategory,
  onComplete,
}: InternalCommentEditCategoryFormProps) {
  const { reportId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState<CatergoryOption | null>(selectedCategory);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly CatergoryOption[]>([]);
  const investigatorNoteCategories = useAppSelector(
    (state) => state.reports.investigatorNoteCategories,
  );
  const internalComments = useAppSelector(
    (state) => state.reportData.report.internalComments,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    setOptions([...investigatorNoteCategories]);
  }, [setOptions, investigatorNoteCategories]);

  useEffect(() => {
    if (
      inputValue !== '' &&
      !investigatorNoteCategories.some((option) => option.name === inputValue)
    ) {
      setOptions([
        ...investigatorNoteCategories,
        {
          id: -1,
          name: inputValue,
          isNewOption: true,
        },
      ]);
    }
  }, [value, inputValue, setOptions, investigatorNoteCategories]);

  return (
    <div className='relative'>
      {isLoading && (
        <div className='absolute top-0 left-0 w-full h-full bg-black bg-opacity-10 flex justify-center items-center z-[1000]'>
          <CircularProgress />
        </div>
      )}
      <div className='flex flex-row gap-2'>
        <div className='flex flex-col'>
          <Autocomplete
            sx={{ width: 300 }}
            getOptionLabel={(option) => option.name}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            isOptionEqualToValue={(option, currentValue) =>
              option.id === currentValue.id
            }
            onChange={(_event, newValue: CatergoryOption | null) => {
              if (!newValue) {
                setOptions(investigatorNoteCategories);
              }

              setValue(newValue);
            }}
            onInputChange={(_event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                variant='standard'
                {...params}
                sx={{
                  border: '1px solid #000000',
                  borderRadius: '6px',
                  padding: '4px 12px',
                  backgroundColor: '#F7F7F7',
                }}
                InputProps={{ ...params.InputProps, disableUnderline: true }}
                label=''
                fullWidth
              />
            )}
            renderOption={(props, option) => {
              return (
                <li {...props}>
                  <Typography sx={{ padding: '6px 16px' }}>
                    {option.name}{' '}
                    {option.isNewOption && (
                      <Typography
                        component='span'
                        sx={{
                          color: '#878787',
                        }}
                      >
                        (New category)
                      </Typography>
                    )}
                  </Typography>
                </li>
              );
            }}
          />
        </div>
        <div className='flex flex-col justify-center'>
          <IconButton
            size='small'
            sx={{
              width: '28px',
              height: '28px',
              flexShrink: 0,
              padding: 0, // Remove default padding
              borderColor: '#33DA7F',
              borderRadius: '5px',
              borderWidth: '3px',
              borderStyle: 'solid',
              backgroundColor: '#33DA7F14',
              '& :hover': {
                borderRadius: '5px',
              },
            }}
            onClick={async () => {
              setIsLoading(true);

              const selected =
                value && value.name.trim() !== '' ? { ...value } : null;
              if (selected && selected.isNewOption) {
                const newCategory =
                  await investigatorNotesServices.createNoteCategory(
                    selected.name,
                  );
                dispatch(
                  setInvestigatorNoteCategories([
                    ...investigatorNoteCategories,
                    newCategory,
                  ]),
                );
                selected.id = newCategory.id;
              }

              if (internalComment.isFromMeetingNotes) {
                await meetingService.updateMeetingNoteCategory(
                  internalComment.id,
                  selected ? selected.id : null,
                );
              } else {
                await investigatorNotesServices.updateInternalCommentCategory(
                  reportId ?? '',
                  internalComment.id,
                  selected ? selected.id : null,
                );
              }

              const updatedInternalComments = internalComments.map(
                (comment) => {
                  if (comment.id === internalComment.id) {
                    return {
                      ...comment,
                      category: selected,
                    };
                  }
                  return comment;
                },
              );
              dispatch(updateReportInternalComments(updatedInternalComments));

              setIsLoading(false);
              onComplete();
            }}
          >
            <Check size={24} color='#33DA7F' />
          </IconButton>
        </div>
        <div className='flex flex-col justify-center'>
          <IconButton
            size='small'
            sx={{
              width: '28px',
              height: '28px',
              flexShrink: 0,
              padding: 0, // Remove default padding
              borderColor: '#FF5E56',
              borderRadius: '5px',
              borderWidth: '3px',
              borderStyle: 'solid',
              backgroundColor: '#FF5E5614',
              '& :hover': {
                borderRadius: '5px',
              },
            }}
            onClick={() => {
              onComplete();
            }}
          >
            <X size={24} color='#FF5E56' />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
export default InternalCommentEditCategoryForm;
